import './LineItemGroup.scss';

import { bem } from 'mission-control-common-components';
import React from 'react';

import AdRow from '../../../components/AdRow/AdRow';
import LineItemGroupHeader from '../../../components/LineItemGroupHeader/LineItemGroupHeader';
import TagRow from '../../../components/TagRow/TagRow';
import type { DragHandlers, TagSheetAd } from '../../../types';
import type { AdValidationError } from '../../../validation/types';
import useLineItemData from './hooks/useLineItemData';

const [block] = bem('line-item-group');

type LineItemGroupProps = {
  groupId: string;
  label: string;
  ads: TagSheetAd[];
  isUnmapped: boolean;
  startDate?: string | null;
  endDate?: string | null;
  billableEvent?: string | null;
  isExpanded: (id: string) => boolean;
  onToggleExpand: (id: string) => void;
  dragHandlers: DragHandlers;
  isDropTarget: boolean;
  updateAds: (updatedAds: TagSheetAd[]) => void;
  adErrors: Record<string, AdValidationError>;
};

const getDropTargetModifier = (isForbiddenDrop: boolean, isDropTarget: boolean): string => {
  if (isForbiddenDrop) {
    return 'drop-target-forbidden';
  }
  if (isDropTarget) {
    return 'drop-target';
  }
  return '';
};

const LineItemGroup = ({
  groupId,
  label,
  ads,
  isUnmapped,
  startDate,
  endDate,
  billableEvent,
  isExpanded,
  onToggleExpand,
  dragHandlers,
  isDropTarget,
  updateAds,
  adErrors,
}: LineItemGroupProps): React.JSX.Element => {
  const { cloneAd, removeAd, addTag, removeTag, editAd, editAdTag } = useLineItemData({ ads, updateAds });

  const isForbiddenDrop = isDropTarget && dragHandlers.dropTargetGroupForbidden;

  return (
    <tbody
      className={block(getDropTargetModifier(isForbiddenDrop, isDropTarget))}
      onDragOver={dragHandlers.onDragOver(groupId)}
      onDragLeave={dragHandlers.onDragLeave}
      onDrop={dragHandlers.onDrop(groupId)}
    >
      <LineItemGroupHeader
        label={label}
        isUnmapped={isUnmapped}
        adCount={ads.length}
        startDate={startDate}
        endDate={endDate}
        isExpanded={isExpanded(groupId)}
        onToggle={(): void => onToggleExpand(groupId)}
      />
      {isExpanded(groupId) &&
        ads.map((ad, adIndex) => {
          const adId = ad.id;

          const handleAddTag = (): void => {
            addTag(adIndex);
            if (!isExpanded(adId)) onToggleExpand(adId);
          };
          const adError = adErrors[adId];

          return (
            <React.Fragment key={adId}>
              <AdRow
                ad={ad}
                dragHandlers={dragHandlers}
                isExpanded={isExpanded(adId)}
                onToggleExpand={(): void => onToggleExpand(adId)}
                onClone={(): void => cloneAd(adIndex)}
                onAddTag={handleAddTag}
                onDelete={(): void => removeAd(adIndex)}
                onEditAd={(updatedAd): void => editAd(adIndex, updatedAd)}
                error={adError}
              />
              {isExpanded(adId) &&
                ad.tags.map((tag, tagIndex) => (
                  <TagRow
                    key={`${adId}-tag-${tagIndex}`}
                    tag={tag}
                    siblingTags={ad.tags}
                    onRemove={(): void => removeTag(adIndex, tagIndex)}
                    onEditTag={(updatedTag): void => editAdTag(adIndex, tagIndex, updatedTag)}
                    error={adError?.tags[tagIndex]}
                    lineItemBillableEvent={billableEvent}
                  />
                ))}
            </React.Fragment>
          );
        })}
    </tbody>
  );
};

export default LineItemGroup;
